import React, { useRef } from "react";
import { ErrorOutlineOutlined } from "@mui/icons-material";
import { WorkspaceUserAvatar } from "./WorkspaceUserHoverCard";

const isPastDue = (value) => {
  if (!value) return false;
  const dueDate = /^\d{4}-\d{2}-\d{2}$/.test(`${value}`) ? new Date(`${value}T00:00:00`) : new Date(value);
  if (Number.isNaN(dueDate.getTime())) return false;
  const dueDay = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return dueDay < todayStart;
};

export function WorkKanbanView({
  activeBoardSelected = false,
  ariaLabel,
  draggedTicketId = "",
  dragLaneKey = "",
  getAssigneeProfile = () => null,
  getDateLabel = () => "",
  hideEmptyWhenBoardSelected = false,
  lanes = [],
  laneAccentMap = {},
  onLaneDragOver = () => {},
  onLaneDrop = () => {},
  onOpenRecord = () => {},
  onTicketDragEnd = () => {},
  onTicketDragStart = () => {},
  renderTypeMark = () => null,
  showLane = () => true,
  sortRecords = (records) => records,
}) {
  const boardRef = useRef(null);
  const visibleLanes = (() => {
    if (!Array.isArray(lanes)) return [];
    if (typeof showLane !== "function") return lanes;
    const maybeFiltered = showLane(lanes);
    return Array.isArray(maybeFiltered) ? maybeFiltered : lanes.filter(showLane);
  })();
  const handleColumnWheel = (event) => {
    const board = boardRef.current;
    if (!board) return;
    const horizontalDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.shiftKey ? event.deltaY : 0;
    if (!horizontalDelta) return;
    const canScrollHorizontally = board.scrollWidth > board.clientWidth;
    if (!canScrollHorizontally) return;
    event.preventDefault();
    board.scrollLeft += horizontalDelta;
  };

  return (
    <div className="functional-kanban is-ticket-board" aria-label={ariaLabel} ref={boardRef}>
      {visibleLanes.map((lane) => {
        const columnRecords = sortRecords(lane.records || []);
        if (hideEmptyWhenBoardSelected && activeBoardSelected && !columnRecords.length) return null;
        return (
          <section
            className={dragLaneKey === lane.key ? "is-drag-target" : ""}
            key={lane.key}
            onDragOver={(event) => onLaneDragOver(event, lane.key)}
            onDrop={(event) => onLaneDrop(event, lane.key)}
            style={{ "--work-board-lane-accent": laneAccentMap[lane.key] || "var(--brand)" }}
          >
            <header>
              <strong>{lane.label}</strong>
              <span>{columnRecords.length}</span>
            </header>
            <div onWheel={handleColumnWheel}>
              {columnRecords.map((record) => {
                const overdue = isPastDue(record.due);
                return (
                <article
                  className={draggedTicketId === `${record.id}` ? "is-dragging" : ""}
                  draggable
                  key={record.id}
                  onClick={() => onOpenRecord(record)}
                  onDragEnd={onTicketDragEnd}
                  onDragStart={(event) => onTicketDragStart(event, record)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onOpenRecord(record);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="work-ticket-card-top">
                    <small>
                      {renderTypeMark(record.type)}
                      <span>{record.key || record.id || "Ticket"}</span>
                    </small>
                    <WorkspaceUserAvatar
                      className="work-ticket-card-avatar"
                      title={record.owner || "Unassigned"}
                      user={getAssigneeProfile(record)}
                    />
                  </div>
                  <div className="work-ticket-card-title">
                    <strong>{record.title}</strong>
                  </div>
                  <div className="work-ticket-card-meta">
                    <span className={`work-ticket-card-date${overdue ? " is-overdue" : ""}`}>
                      {overdue ? <span className="work-ticket-card-overdue-icon" title="Overdue"><ErrorOutlineOutlined aria-hidden="true" fontSize="small" /><span className="sr-only">Overdue</span></span> : null}
                      {getDateLabel(record.due)}
                    </span>
                    <span className={`work-ticket-card-priority is-${`${record.priority || "Medium"}`.toLowerCase().replace(/\s+/g, "-")}`}>{record.priority || "Medium"}</span>
                  </div>
                  {record.sprint || record.epic ? (
                    <div className="work-ticket-card-context">
                      {record.sprint ? <em>{record.sprint}</em> : null}
                      {record.epic ? <em>{record.epic}</em> : null}
                    </div>
                  ) : null}
                </article>
                );
              })}
              {!columnRecords.length ? <p>No tickets</p> : null}
            </div>
          </section>
        );
      })}
    </div>
  );
}
